import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaidIcon from "@mui/icons-material/Paid";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useNavigate } from "react-router-dom";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";
import PaginationControls from "../Pagination/PaginationControls";

const PAGE_SIZE = 50;

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

const InvestorMarketingSpend = () => {
  const navigate = useNavigate();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo, setAppliedTo] = useState("");

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [appliedFrom, appliedTo]);

  useEffect(() => {
    const fetchSpendData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (appliedFrom) params.append("from_date", appliedFrom);
        if (appliedTo) params.append("to_date", appliedTo);
        params.append("page", page);
        const res = await axiosConfig({ method: "get", url: `/investors/marketing-expense-data/?${params}` });
        const raw = res?.data;
        setTotalCount(raw?.count || 0);
        setRows(raw?.results || (Array.isArray(raw) ? raw : []));
      } catch (e) {
        toast.error(e.response?.data?.detail || e.response?.data?.message || e.message || "Failed to fetch spend data");
      } finally {
        setLoading(false);
      }
    };
    fetchSpendData();
  }, [appliedFrom, appliedTo, page]);

  const handleFilter = () => {
    setAppliedFrom(fromDate);
    setAppliedTo(toDate);
  };

  // Unique channels from current page data
  const uniqueChannels = useMemo(() => {
    const seen = new Set();
    const result = [];
    rows.forEach((r) => {
      if (r.channel_name && !seen.has(r.channel_name)) {
        seen.add(r.channel_name);
        result.push(r.channel_name);
      }
    });
    return result;
  }, [rows]);

  // Group rows by date
  const groupedRows = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      if (!map.has(row.date)) map.set(row.date, new Map());
      map.get(row.date).set(row.channel_name, row);
    });
    return Array.from(map.entries()).map(([date, chMap]) => ({ date, chMap }));
  }, [rows]);

  const serialOffset = (page - 1) * PAGE_SIZE;
  const colCount = 2 + uniqueChannels.length;

  const fieldSx = {
    width: 160,
    "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13, background: "#fff" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  };

  return (
    <Box sx={{ background: "#f1f5f9", minHeight: "100vh" }}>
      {/* Sticky Header */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 100, background: "#fff", borderBottom: "1px solid #e2e8f0", px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            onClick={() => navigate("/investor")}
            sx={{ width: 34, height: 34, borderRadius: "8px", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { background: "#f1f5f9" } }}
          >
            <ArrowBackIcon sx={{ fontSize: 18, color: "#64748b" }} />
          </Box>
          <Box sx={{ width: 38, height: 38, borderRadius: "10px", background: "#04343a", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <PaidIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>Marketing Spend</Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>Date-wise channel spend breakdown</Typography>
          </Box>
        </Box>

        {/* Date filter */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <TextField
            type="date" size="small" label="From" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          <TextField
            type="date" size="small" label="To" value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          <Button
            variant="contained" size="small"
            startIcon={<FilterAltIcon sx={{ fontSize: 16 }} />}
            onClick={handleFilter}
            sx={{ height: 36, borderRadius: "8px", background: "#04343a", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2, "&:hover": { background: "#016551" } }}
          >
            Filter
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid #e2e8f0", background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden" }}>

          {/* Card header strip */}
          <Box sx={{ px: 2.5, py: 1.8, background: "#04343a", borderRadius: "14px 14px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PaidIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 16 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", letterSpacing: 0.5 }}>MARKETING SPEND</Typography>
            </Box>
            {!loading && (
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>
                {totalCount.toLocaleString("en-IN")} record{totalCount !== 1 ? "s" : ""}
                {(appliedFrom || appliedTo) && (
                  <Box component="span" sx={{ ml: 1.5, opacity: 0.75 }}>
                    {appliedFrom && formatDate(appliedFrom)}{appliedFrom && appliedTo && " – "}{appliedTo && formatDate(appliedTo)}
                  </Box>
                )}
              </Typography>
            )}
          </Box>

          {/* Table */}
          <TableContainer sx={{ overflowX: "auto", maxHeight: "calc(100vh - 220px)" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}>S.No</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}>Date</TableCell>
                  {uniqueChannels.map((ch) => (
                    <TableCell key={ch} sx={{ fontSize: 11, fontWeight: 700, color: "#016551", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}>
                      {ch}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={colCount} sx={{ textAlign: "center", py: 6 }}>
                      <CircularProgress size={28} sx={{ color: "#04343a" }} />
                    </TableCell>
                  </TableRow>
                ) : groupedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colCount} sx={{ textAlign: "center", py: 5, fontSize: 13, color: "#94a3b8" }}>
                      No spend records found
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {groupedRows.map(({ date, chMap }, i) => (
                      <TableRow key={date} sx={{ "&:hover": { background: "#f8fafc" } }}>
                        <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{serialOffset + i + 1}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#475569", whiteSpace: "nowrap" }}>
                          {formatDate(date)}
                        </TableCell>
                        {uniqueChannels.map((ch) => {
                          const record = chMap.get(ch);
                          return (
                            <TableCell key={ch} sx={{ whiteSpace: "nowrap" }}>
                              {record ? (
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                                  {formatCurrency(record.amount)}
                                </Typography>
                              ) : (
                                <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>—</Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}

                    {/* Page Total */}
                    <TableRow sx={{ background: "#f0fdf4" }}>
                      <TableCell colSpan={2} sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", borderTop: "2px solid #e2e8f0" }}>
                        Page Total
                      </TableCell>
                      {uniqueChannels.map((ch) => {
                        const total = rows
                          .filter((r) => r.channel_name === ch)
                          .reduce((s, r) => s + parseFloat(r.amount || 0), 0);
                        return (
                          <TableCell key={ch} sx={{ fontSize: 13, fontWeight: 800, color: "#016551", borderTop: "2px solid #e2e8f0", whiteSpace: "nowrap" }}>
                            {formatCurrency(total)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && totalCount > 0 && (
            <PaginationControls totalCount={totalCount} page={page} setPage={setPage} offset={PAGE_SIZE} />
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default InvestorMarketingSpend;
